import { diceCountFrom, dieLabel, MAX_DICE_BOX } from './diceCount.js';
import { X_CARD_LABEL, xCardPreview } from './xCard.js';
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import type { VerbDescriptor } from '@portal/shared';

import { api } from '../api.js';
import { useSocket } from '../realtime/socket.js';
import { DM_AUTHOR, isObjectable } from '../realtime/messages.js';
import { VerbBar, useActionCatalogue } from '../verbs/VerbBar.js';
import { CheckBar, checkPayload } from '../verbs/CheckBar.js';
import { CombatPanel, declaringFor, retainersPresent } from '../combat/CombatPanel.js';
import { MiniMap } from '../map/MiniMap.js';
import { WorldMap } from '../map/MapPanel.js';
import { MapAdminPanel } from '../map/MapAdminPanel.js';
import { usePlaces, waypointsOf } from '../map/places.js';
import { useTerrainGrid } from '../map/terrain.js';
import { SpellsPanel } from '../spells/SpellsPanel.js';
import { unspent, useSpellSheet, type CopyResult } from '../spells/sheet.js';
import { TableHeader } from './TableHeader.js';
import { NotesPanel } from './NotesPanel.js';
import { ShopsPanel } from './ShopsPanel.js';
import { TablePanel } from './TablePanel.js';
import { SettingsPanel } from './SettingsPanel.js';
import { SheetLink } from './SheetLink.js';
import { DigestPanel, StoryPanel } from './DigestPanel.js';
import { ObjectionConfirmDialog, ObjectionPanel } from './ObjectionPanel.js';
import { currentRoom, DungeonRoomPanel, reachableFrom } from './DungeonRoomPanel.js';
import { HenchmenPanel, type HenchmenView } from './HenchmenPanel.js';
import { HirelingsPanel } from './HirelingsPanel.js';
import { useCharacterOptions, useHenchmen } from './henchmen.js';
import { Mention } from './Mention.js';
import { SpiritPanel } from './SpiritPanel.js';
import { LeaveTable, RemoveSeat } from './SeatPanel.js';
import { verbCostLabel, verbLabel } from '../verbs/cost.js';
import { readSlashCommand, slashSuggestions } from '../verbs/slash.js';
import {
  byType,
  partyTargets,
  targetsFor,
  useCanonTargets,
  useCarriedTargets,
  useOwnSkillNames,
} from '../verbs/targets.js';

/**
 * How a station reads in the party list.
 *
 * A canon place id is not something to show a player, so anything that is not
 * one of the two known words reads as simply *away* — the transcript and the
 * DM say where.
 */
function stationLabel(station: string): string {
  if (station === 'ahead') return 'scouting ahead';
  if (station === 'behind') return 'bringing up the rear';
  return 'away from the party';
}

/**
 * Whether this roster entry speaks thieves' cant.
 *
 * **Split on the slash.** The roster renders a multi-classed character as
 * `fighter/thief`, so an equality test against `'thief'` hides the control
 * from exactly the character the book gives it to — the compound-name trap,
 * in the direction where a player simply cannot find their own class ability.
 *
 * Presentation only: the server decides for real, from the class rows, and
 * refuses by name. This is what puts the option on screen.
 */
/**
 * The audience-select value that means cant.
 *
 * Not a character id, and it cannot collide with one: every other value in
 * that select is a uuid.
 */
const CANT = 'thieves-cant';

export function speaksCantOnRoster(characterClass: string | undefined): boolean {
  return (characterClass ?? '').split('/').some((name) => name.trim() === 'thief');
}

/**
 * Every henchman at the table, whoever their patron is — flattened out of
 * `HenchmenView.patrons`, id and name only.
 *
 * ⚠ **A henchman holds no seat, so `roster` (built from `campaignCharacters`)
 * can never list one.** The proposal subject picker used to offer only
 * `roster`, which made "Offer a place at the table" — one of the four things
 * the table votes on, and the one judgement whose subject is *never* a seated
 * party member — impossible to submit for anybody, on any table: there was
 * never a henchman in the dropdown to pick. A promotion may be proposed by
 * anybody, not only the henchman's own patron (who is the one who then has to
 * *accept* it), which is why this is every henchman rather than the viewer's
 * own retainers.
 */
export function allRetainersOf(
  henchmen: HenchmenView | null,
): readonly { readonly characterId: string; readonly name: string }[] {
  return (henchmen?.patrons ?? []).flatMap((patron) =>
    patron.henchmen.map((held) => ({ characterId: held.id, name: held.name })),
  );
}

/**
 * The page's own views, as tabs — and each one gets the FULL page, the same
 * way the transcript already does.
 *
 * **A thin strip spanning the whole width, not a widget confined inside one
 * column.** Tabbing the *contents* of a narrow sidebar while leaving the
 * sidebar itself narrow does not fix crampedness, it relocates it — the
 * strip has to sit above the two-column split, not inside either half of
 * it. `'table'` is the tab that reproduces today's whole two-column view
 * (transcript+composer on the left, roster and notices on the right); every
 * other tab replaces that entirely, full width, nothing else sharing the
 * row. `'map'` benefits the most: a spatial surface a narrow strip could
 * never do justice to, and is not `MapAdminPanel` by itself — see
 * `sidebarTabsFor` and the Map tab's own render, which adds `WorldMap`
 * (viewable by everyone, independent of the shared in-scene `mapMode`)
 * beside it.
 */
const SIDEBAR_TABS = ['table', 'map', 'henchmen', 'hirelings', 'spells', 'settings', 'notes'] as const;
type SidebarTab = (typeof SIDEBAR_TABS)[number];

const SIDEBAR_TAB_LABELS: Readonly<Record<SidebarTab, string>> = {
  table: 'Table',
  map: 'Map',
  henchmen: 'Henchmen',
  hirelings: 'Hirelings',
  spells: 'Spells',
  settings: 'Settings',
  notes: 'Notes',
};

/**
 * Which tabs this viewer actually has.
 *
 * **`spells`** — a spirit has no `SpellsPanel` to show (a dead character
 * casts nothing), the same gate the panel itself carried before it was a
 * tab. **`settings`** — `SettingsPanel` is Owner-only by its own docstring
 * (`PATCH …/config` is `requireOwner`), so a non-owner given the tab would
 * see a form that reads live config and cannot save it — the exact UX cost
 * `MapAdminPanel` was pulled out from behind for the same reason. Pure and
 * exported so a tab list that quietly grows or shrinks is something a test
 * can assert on without mounting the whole page.
 */
export function sidebarTabsFor(input: {
  readonly amSpirit: boolean;
  readonly amOwner: boolean;
}): readonly SidebarTab[] {
  return SIDEBAR_TABS.filter(
    (tab) => (tab !== 'spells' || !input.amSpirit) && (tab !== 'settings' || input.amOwner),
  );
}

/** The live table: transcript, roster, composer, verb bar and the dice bar. */
/**
 * The server's vocabulary, in the table's words.
 *
 * Presentation only — the values are the engine's and are refused by name if
 * wrong. An unmapped key renders as itself rather than vanishing, so a
 * surrounding added to the data shows up looking odd instead of not at all.
 */
const GROUND_WORDS: Readonly<Record<string, string>> = {
  naturalStone: 'natural stone',
  underground: 'underground',
  naturalSurroundings: 'open country',
};

/**
 * The four things a table can be asked to agree to, each about one character.
 *
 * `help` is what the button does, said for a player. The kinds and their rules are the server's
 * (`director/proposals.ts` `whyNotProposable` refuses an unworkable one, and carrying one out checks
 * who it may be about); this only keeps the form from offering a combination it knows will fail.
 */
const PROPOSAL_BUTTONS = [
  { kind: 'award_xp', label: 'Award experience', help: 'The table votes on giving this character experience points.' },
  { kind: 'complete_practice', label: 'Finish training', help: 'The table votes on counting this character’s training as finished.' },
  { kind: 'offer_promotion', label: 'Offer a place at the table', help: 'The table votes on offering this henchman a place at the table in their own right.' },
  { kind: 'raise_stronghold', label: 'Raise a stronghold', help: 'The table votes on this character raising a stronghold of their own.' },
] as const;

/** Why a proposal button cannot be pressed for this choice, or undefined when it can. */
export function proposalBlocker(
  kind: (typeof PROPOSAL_BUTTONS)[number]['kind'],
  subject: string,
  subjectIsHenchman: boolean,
  amount: string,
): string | undefined {
  if (kind === 'award_xp') {
    const points = Number.parseInt(amount, 10);
    if (!Number.isInteger(points) || points === 0) return 'Enter how much experience to award first.';
    return subjectIsHenchman ? 'A henchman can only be offered a place at the table.' : undefined;
  }
  if (subject === '') {
    return kind === 'offer_promotion' ? 'Choose which henchman first.' : 'Choose whose character this is about first.';
  }
  if (kind === 'offer_promotion') {
    return subjectIsHenchman ? undefined : 'A place at the table is offered to a henchman. Choose one.';
  }
  if (subjectIsHenchman) return 'A henchman can only be offered a place at the table.';
  return undefined;
}

export function CampaignPage() {
  const { campaignId } = useParams();
  const connect = useSocket((s) => s.connect);
  const disconnect = useSocket((s) => s.disconnect);
  const say = useSocket((s) => s.say);
  const submitAction = useSocket((s) => s.submitAction);
  const rollDice = useSocket((s) => s.rollDice);
  // How many dice the next click rolls. Kept as text so the box can be cleared and retyped.
  const [diceCountText, setDiceCountText] = useState('1');
  const diceCount = diceCountFrom(diceCountText);
  const rollCheck = useSocket((s) => s.rollCheck);
  const whisper = useSocket((s) => s.whisper);
  const speakCant = useSocket((s) => s.speakCant);
  const raiseVeto = useSocket((s) => s.raiseVeto);
  const undoVeto = useSocket((s) => s.undoVeto);
  // The X-card's own state: hovering/focusing it highlights the line it would skip, and for a few seconds after a
  // press the player may take it back.
  const [vetoPreview, setVetoPreview] = useState(false);
  const [vetoUndoOpen, setVetoUndoOpen] = useState(false);
  useEffect(() => {
    if (!vetoUndoOpen) return undefined;
    const timer = setTimeout(() => setVetoUndoOpen(false), 15_000);
    return () => clearTimeout(timer);
  }, [vetoUndoOpen]);
  const setPresence = useSocket((s) => s.setPresence);
  const state = useSocket((s) => s.state);
  const messages = useSocket((s) => s.messages);
  const roster = useSocket((s) => s.roster);
  const promptDm = useSocket((s) => s.promptDm);
  // **How the party is carrying itself, for a `talk`.** The players', not a
  // The players': Table 10.7 is read against "the PCs' disposition toward the
  // opposing party", and the party is the only one who knows that. Blank is
  // indifferent — walking up and speaking.
  const [stance, setStance] = useState('');
  const dm = useSocket((s) => s.dm);
  const combat = useSocket((s) => s.combat);
  const declareCombat = useSocket((s) => s.declareCombat);
  const myCharacterId = useSocket((s) => s.myCharacterId);
  const saveNotes = useSocket((s) => s.saveNotes);
  const hostCommand = useSocket((s) => s.hostCommand);
  const revealed = useSocket((s) => s.revealed);
  const surroundings = useSocket((s) => s.surroundings);
  const sceneFeatures = useSocket((s) => s.sceneFeatures);
  const timeSkip = useSocket((s) => s.timeSkip);
  const mapMode = useSocket((s) => s.mapMode);
  const map = useSocket((s) => s.map);
  const partyLocation = useSocket((s) => s.partyLocation);
  const sheetNotice = useSocket((s) => s.sheetNotice);
  const vetoNotice = useSocket((s) => s.vetoNotice);
  const openObjection = useSocket((s) => s.openObjection);
  const openProposal = useSocket((s) => s.openProposal);
  const voteOnProposal = useSocket((s) => s.voteOnProposal);
  const propose = useSocket((s) => s.propose);
  const [proposeSubject, setProposeSubject] = useState('');
  const [proposeAmount, setProposeAmount] = useState('');
  const objectionNotice = useSocket((s) => s.objectionNotice);
  const raiseObjection = useSocket((s) => s.raiseObjection);
  const claimObjectionLink = useSocket((s) => s.claimObjectionLink);
  const objectionClaim = useSocket((s) => s.objectionClaim);
  const clearObjectionClaim = useSocket((s) => s.clearObjectionClaim);
  const submitObjectionGrounds = useSocket((s) => s.submitObjectionGrounds);
  const voteOnObjection = useSocket((s) => s.voteOnObjection);
  const [confirmingObjection, setConfirmingObjection] = useState<string | null>(null);
  const sheetVersion = useSocket((s) => s.sheetVersion);
  const memoriseSpells = useSocket((s) => s.memoriseSpells);
  const castSpell = useSocket((s) => s.castSpell);
  const clock = useSocket((s) => s.clock);
  const pending = useSocket((s) => s.pending);
  const lastError = useSocket((s) => s.lastError);
  const [dmAsk, setDmAsk] = useState('');
  const digest = useSocket((s) => s.digest);
  const story = useSocket((s) => s.story);
  const dismissDigest = useSocket((s) => s.dismissDigest);
  const clearError = useSocket((s) => s.clearError);

  const { verbs, combatActions, reactionDispositions } = useActionCatalogue();
  // Per-viewer, so it comes over HTTP rather than the room-wide socket — and
  // is re-asked when fog moves, which is the event that changes the answer.
  const { places, lines: mapLines, reload: reloadPlaces } = usePlaces(
    campaignId,
    revealed,
    partyLocation,
  );
  // Room-wide and static between imports, so it is fetched once on the campaign
  // id alone — deliberately not folded into `usePlaces`, which is re-asked every
  // time fog or the party moves and would drag the largest column in the schema
  // along with it each time.
  //
  // Asked for only while the world map is the surface, because it is the only
  // one that draws it: Theatre of the Mind is the default and a first-class
  // surface, and every member of every imported table paying tens of kilobytes
  // on load for a picture nothing renders is the cost this read exists to avoid.
  // ⚠ No `reload` any more: the world import moved off this surface (#4a), so
  // nothing here can change the terrain mid-session. Coming back from the map
  // setup page remounts this one, which fetches it fresh.
  const { grid: terrain } = useTerrainGrid(
    mapMode === 'worldMap' ? campaignId : undefined,
  );
  // Per-viewer, like the places list, and re-asked whenever the server may
  // have rewritten the loadout: a slot spent in a combat round is spent on the
  // server, and a stale panel would offer it again.
  // A monotonic counter, not the notice text: the notice is the same sentence
  // for two ordinary changes, so watching it would miss the second cast of a
  // round and keep offering a slot the server has already spent.
  // **Whose spells the combat form offers.** A retainer's loadout is not the
  // viewer's, so the acting character decides which sheet is fetched — and the
  // choice therefore has to live here rather than inside the panel. `undefined`
  // is the viewer's own, which is every ordinary round.
  const [actingChoice, setActingChoice] = useState<{
    round: number;
    characterId: string;
  } | null>(null);
  // **Where a `move` declaration is headed.** The click lands on `MapPanel`,
  // a sibling of `CombatPanel`, so the two can only agree through this page —
  // the same reason `actingChoice` lives here rather than inside either
  // panel. `needsDestination` arms the map's `onPickPoint` only while `move`
  // is actually the selected action; armed all the time, an ordinary "where
  // are we" click on the shared map would quietly become a declaration.
  const [pendingDestination, setPendingDestination] = useState<{
    mapX: number;
    mapY: number;
  } | null>(null);
  const [needsDestination, setNeedsDestination] = useState(false);
  const [verb, setVerb] = useState<VerbDescriptor | undefined>(undefined);
  const [draft, setDraft] = useState('');
  const [targetId, setTargetId] = useState('');
  // What the check bar has selected: an ability abbreviation or a skill name.
  const [checking, setChecking] = useState('');
  const [blindCheck, setBlindCheck] = useState(false);
  const [groupCheck, setGroupCheck] = useState(false);
  // Who the next line is for. Empty is the whole table, which is what table
  // talk normally is.
  const [whisperTo, setWhisperTo] = useState('');
  const [picked, setPicked] = useState<{ mapX: number; mapY: number } | undefined>(undefined);
  // Re-asked when fog moves (a reveal makes a place knowable), when the party moves (an object lies at
  // ONE place), when a sheet changes (taking or dropping moves an object), and when the DM or the engine
  // speaks (that is when an object appears, and the line says so). It was keyed on fog alone, so a list
  // went stale the moment anything in it changed.
  // The description the X-card would skip: the newest DM line this player can see (what the server records) -
  // including the beat still streaming, which the server records from what it has sent so far.
  const lastDmLine = [...messages].reverse().find((m) => m.author === DM_AUTHOR && !m.voided && m.text.trim() !== '');
  const worldBeats = messages.filter((m) => m.author === DM_AUTHOR || m.kind === 'system' || m.author === 'engine').length;
  const canonTargets = useCanonTargets(campaignId, `${revealed.length}|${partyLocation ?? ''}|${sheetVersion}|${worldBeats}`);
  // Re-asked when the sheet changes, because taking something is what most
  // often makes a new thing readable.
  const carriedTargets = useCarriedTargets(myCharacterId, sheetVersion);
  const ownSkills = useOwnSkillNames(myCharacterId, sheetVersion);
  const suggestions = slashSuggestions(draft, verbs);
  // Which entries each verb offers is `targetsFor`'s one table. The party joins the
  // list for a pick-pocket only, and a character's own pack for a read only; whether
  // the table allows a theft is the server's answer, not the picker's.
  const targets = targetsFor(verb?.verb, {
    canon: canonTargets,
    party: partyTargets(roster, myCharacterId),
    carried: carriedTargets,
    // In a dungeon room a move can only take one of its exits.
    reachable: reachableFrom(places, partyLocation ?? undefined),
  });
  const bottom = useRef<HTMLLIElement>(null);
  // **Not keyed on the transcript.** It was `messages.length`, which changes on
  // every chat line, every system line and twice per narration beat — so every
  // connected client re-read the whole retainer list, three queries a piece, on
  // every message, at a table that may retain nobody at all.
  //
  // `henchmenVersion` covers this client's own hires and releases.
  // `sheetVersion` covers the one thing that changes the list from the server:
  // a henchman outgrowing their patron, which only happens on an experience
  // award — and an award that reaches a retainer reached their patron in the
  // same loop, so this player's own sheet moved with it.
  const [henchmenVersion, setHenchmenVersion] = useState(0);
  // `combat?.round` is in the token because a retainer takes wounds and can be
  // killed in a round, and none of that reaches this panel any other way — the
  // roster is seat-only and a henchman holds no seat. Per *round*, not per
  // message: a bounded handful of reads over a fight, where the transcript
  // would be one per line.
  const henchmen = useHenchmen(
    campaignId,
    `${henchmenVersion}:${sheetVersion}:${combat?.round ?? 'peace'}`,
  );
  const amOwner = roster.find((entry) => entry.characterId === myCharacterId)?.isOwner === true;
  // Fetched only for somebody who can actually hire: the option payload is the
  // whole character wizard's — every race, every class, every printed
  // generation method — and a player who will never see the form should not
  // pay for it on every campaign load.
  // **Fetched for everyone.** It carries the hiring tables *and* the skill
  // catalogue, and a check is something any player makes.
  const hireOptions = useCharacterOptions(true);
  // This player's own retainers, by id and name. The combat panel narrows them
  // again to the ones actually standing in the fight.
  const myRetainers = (
    henchmen?.patrons.find((patron) => patron.characterId === myCharacterId)?.henchmen ?? []
  ).map((held) => ({ characterId: held.id, name: held.name }));
  // See `allRetainersOf`'s own docstring — this is what feeds the proposal
  // subject picker below, the one place it is actually needed.
  const allHenchmen = allRetainersOf(henchmen);
  // **Narrowed to this fight, once, here.** The panel used to filter for its
  // picker while this line ran the same rule over the unfiltered list, and the
  // two could disagree: a choice left from an earlier fight had this fetching a
  // retainer's spell sheet while the panel declared for the viewer's own
  // character, so a spell picked off the retainer's list was submitted as the
  // patron's. One list, one derivation, passed down.
  const retainersInFight = retainersPresent(myRetainers, combat);
  const actingAs = declaringFor(actingChoice, combat?.round ?? 0, retainersInFight);

  // **Two sheets, deliberately.** The viewer's own is what the Spells panel
  // renders and writes through — its Study button emits no `characterId`, so a
  // panel showing a retainer's loadout would have been labelled wrongly *and*
  // written to the wrong character. The acting one is only for the combat form.
  const { sheet: ownSpells, reload: reloadOwnSpells } = useSpellSheet(campaignId, sheetVersion);
  // Whose loadout the Spells panel is showing, out of combat. Separate from
  // `actingChoice`, which is the *combat* form's and is cleared with the fight.
  const [showingSpells, setShowingSpells] = useState('');
  const { sheet: retainerSpells, reload: reloadRetainerSpells } = useSpellSheet(
    campaignId,
    `${sheetVersion}:${showingSpells}`,
    showingSpells || undefined,
  );
  const { sheet: actingSpells } = useSpellSheet(
    campaignId,
    `${sheetVersion}:${actingAs}`,
    actingAs || undefined,
  );
  const spells = actingAs ? actingSpells : ownSpells;
  // Every beat is third person for the whole table; this is how a player finds
  // themselves in it. Only ever their *own* name, so a line lights up for one
  // reader and stays plain for everybody else.
  const myName = roster.find((entry) => entry.characterId === myCharacterId)?.name ?? null;
  const amThief = speaksCantOnRoster(
    roster.find((entry) => entry.characterId === myCharacterId)?.characterClass,
  );
  // Everybody else at the table. A whisper to yourself is not a thing, and a
  // dead player is a spirit — `requireLiving` refuses their chat, so offering
  // them as a *recipient* is fine and offering the control to them is not.
  const whisperTargets = roster.filter(
    (entry) => entry.characterId !== myCharacterId && entry.deathStatus !== 'dead',
  );

  /**
   * This player is a spirit: seen, never heard.
   *
   * **The whole action surface goes with it.** `requireLiving` refuses
   * `chat:say`, `chat:whisper`, `chat:cant`, `dice:roll`, `skill:check`,
   * `action:submit`, `spell:memorise` and `spell:cast` from a dead character —
   * so every one of those controls is a button whose only possible outcome is a
   * refusal. Found by looking at the screen: the spirit panel said "you cannot
   * act, signal or speak" while the composer, the verb bar, the check bar and
   * seven dice sat under it, enabled. That is the same defect as offering a
   * character for a seat the server would 409.
   *
   * The transcript stays, and so do the notes — a spirit still *sees*, and
   * writing in your own notebook is the player's rather than the character's.
   */
  const amSpirit =
    roster.find((entry) => entry.characterId === myCharacterId)?.deathStatus === 'dead';

  const availableSidebarTabs = sidebarTabsFor({ amSpirit, amOwner });
  const [sidebarTab, setSidebarTab] = useState<SidebarTab>('table');
  // A character can die mid-session, taking the Spells tab with it — caught
  // here rather than left to render an empty pane nobody asked to see.
  useEffect(() => {
    if (!availableSidebarTabs.includes(sidebarTab)) setSidebarTab('table');
  }, [availableSidebarTabs, sidebarTab]);

  useEffect(() => {
    if (!campaignId) return;
    connect(campaignId);
    return () => disconnect();
  }, [campaignId, connect, disconnect]);

  // Presence follows the tab: a backgrounded tab is not someone at the table.
  useEffect(() => {
    const onVisibility = () => setPresence(document.hidden ? 'idle' : 'online');
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [setPresence]);

  useEffect(() => {
    bottom.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  // **The Object link's own courtesy clock.** Coarse (5s) since this is
  // cosmetic — the server is the real gate — and self-clearing: once every
  // visible line has aged out of `OBJECT_LINK_WINDOW_MS`, there is nothing
  // left to re-check and the ticker stops rather than running forever.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!messages.some((m) => isObjectable(m, Date.now()))) return;
    const id = setInterval(() => setNow(Date.now()), 5000);
    return () => clearInterval(id);
  }, [messages]);

  // **A fight ending clears who you were declaring for.** While this state
  // lived inside `CombatPanel` the component unmounted with the fight and did
  // this for free; lifted to the page, nothing did — and `declaringFor` guards
  // on the round *number*, which restarts at 1 every fight, so a choice from one
  // battle was silently live in the first round of the next. The narrowing to
  // `retainersInFight` is what actually makes that safe; this is what stops it
  // being load-bearing.
  useEffect(() => {
    if (!combat) setActingChoice(null);
  }, [combat]);

  // A verb chosen before the fight started would sit in the composer waiting
  // to be sent into a vocabulary the server no longer accepts.
  useEffect(() => {
    if (combat) {
      setVerb(undefined);
      setTargetId('');
    }
  }, [combat]);

  // A target chosen under one verb does not belong to the next: the party is
  // offered for a pick-pocket and nothing else, so a switch to `search` would
  // otherwise carry a character id into a verb that has no use for one.
  useEffect(() => setTargetId(''), [verb?.verb]);

  return (
    // **The tab strip owns the FULL page width, and so does whichever tab is
    // active below it — the same way the transcript already fills its own
    // column.** "Table" is the tab that reproduces today's whole two-column
    // view (transcript+composer on the left, roster and notices on the
    // right) — everything below was never rewritten, only moved a level
    // deeper and gated on `sidebarTab === 'table'`. Every other tab (Map
    // above all — a spatial surface a narrow strip could never do justice
    // to) gets that entire width to itself, full-bleed, with nothing else
    // sharing the row.
    <div className="flex flex-col gap-3 md:h-full md:min-h-0">
      <div
        role="tablist"
        aria-label="Table views"
        className="flex flex-wrap gap-1 border-b border-neutral-200 pb-2 dark:border-neutral-800"
      >
        {availableSidebarTabs.map((tab) => (
          <button
            key={tab}
            type="button"
            role="tab"
            aria-selected={sidebarTab === tab}
            onClick={() => setSidebarTab(tab)}
            className={`rounded-md px-2 py-1 text-xs font-medium ${
              sidebarTab === tab
                ? 'bg-neutral-800 text-white dark:bg-neutral-200 dark:text-neutral-900'
                : 'border border-neutral-300 text-neutral-600 dark:border-neutral-700 dark:text-neutral-400'
            }`}
          >
            {SIDEBAR_TAB_LABELS[tab]}
          </button>
        ))}
      </div>

      <div role="tabpanel" className="md:min-h-0 md:flex-1">
      {sidebarTab === 'table' && (
    // Fills the shell from `md` up so the two columns scroll independently.
    // Below that the layout is one column and a normally-scrolling page is the
    // right answer — a phone has no room for a pane that scrolls inside a pane.
    <div className="grid gap-6 md:h-full md:min-h-0 md:grid-cols-[minmax(0,1fr)_20rem]">
      <section className="flex min-h-[24rem] flex-col gap-3 md:min-h-0">
        <TableHeader
          clock={clock}
          mode={combat ? 'combat' : 'exploration'}
          round={combat?.round}
          dm={dm}
          connected={state === 'connected'}
        />

        {/* Above the transcript, because it is about the beats *below* it —
            and before anything the player might act on. */}
        {/* For a player with no scrollback — a newcomer, or one who has just
            rerolled. Everyone else is reading the beats themselves. */}
        {/* **Scenery, shown to the whole table.** The DM declares what kind
            of place this is on every beat and the engine turns that into the
            printed preconditions; this is the one line that says so out loud.
            Without it a dwarf whose stone sense has gone quiet has nothing on
            screen explaining why. */}
        {surroundings.length > 0 && (
          <p className="text-xs text-neutral-500 dark:text-neutral-400">
            Underfoot: {surroundings.map((value) => GROUND_WORDS[value] ?? value).join(', ')}
          </p>
        )}

        {/* **The undo the announcement is telling them about.** A skip of
            half a day or more is offered back until anybody acts — so the
            control has to survive a reload, which is why it rides the snapshot
            rather than the line that announced it. Any player, deliberately:
            time is one integer and taking it back costs nothing, where a vote
            would be one person at 2am blocking an async table. */}
        {timeSkip && (
          <div className="flex flex-wrap items-center gap-2 rounded-md border border-amber-500/40 bg-amber-50 p-2 text-sm dark:bg-amber-950/30">
            <span>{timeSkip.label} passed.</span>
            <button
              type="button"
              onClick={() => {
                void api
                  .post(`/api/campaigns/${campaignId}/time/undo`, {})
                  .catch(() => undefined);
              }}
              className="rounded-md border border-neutral-300 px-2 py-0.5 text-xs dark:border-neutral-700"
            >
              Take it back
            </button>
            <span className="text-xs text-neutral-500 dark:text-neutral-400">
              until somebody acts
            </span>
          </div>
        )}

        <StoryPanel story={story} transcriptLength={messages.length} />
        <DigestPanel digest={digest} onDismiss={dismissDigest} />
        <DungeonRoomPanel
          room={currentRoom(places, partyLocation ?? undefined)}
          onMove={(targetId) => submitAction('move', '', targetId, undefined)}
        />


        {/* The only thing that scrolls in the left column. `min-h-0` again:
            without it this grows and pushes the composer off the bottom. */}
        {/* **`tabIndex` because it scrolls.** A scrollable region that cannot
            take focus cannot be scrolled by keyboard at all, and this is the
            transcript — somebody reading back through what happened is the
            single most ordinary thing anyone does here. axe flags it as
            serious and it is right to. The label names it, or a screen reader
            announces a focusable region with nothing to say about it. */}
        {/* **The map is a corner minimap over the story, not a block above it** (Maz, 2026-10-20: story-first).
            Top-right, because the newest lines are at the bottom and must never sit under it. */}
        <div className="relative flex min-h-[8rem] flex-1 flex-col">
          <div className="absolute right-2 top-1 z-10">
        <MiniMap
          mode={mapMode}
          map={map}
          waypoints={waypointsOf(places)}
          lines={mapLines}
          terrain={terrain}
          partyLocation={partyLocation ?? undefined}
          combat={combat}
          // Opens by itself while the combat form has `move` selected and no point is picked yet — the
          // destination is chosen by clicking the map, and a thumbnail cannot take a click.
          forceOpen={needsDestination && pendingDestination === null}
          {...(needsDestination ? { onPickPoint: setPendingDestination } : {})}
          // Pins ask "travel there?" and go only when confirmed — exploration only: in a fight a move is a point on
          // the encounter map, not a journey.
          {...(campaignId && combat === null ? { campaignId, onTravel: (placeId: string) => submitAction('move', '', placeId, undefined) } : {})}
        />
          </div>
        <ol
          tabIndex={0}
          aria-label="Transcript"
// ⚠ **A floor, and the reason it is safe now is not the reason it was
          // unsafe then.** This collapsed to 22px once; flooring it at the time
          // only traded a sliver for a page that jumped to empty space on every
          // beat, because `main` was *also* scrolling while this page asks for
          // `md:h-full`, and the two disagreed. The answer then was to stop the
          // shell scrolling on this route — and measuring it on 2026-08-29
          // showed that rule had been inert for some time, so it is gone and
          // `main` is an ordinary `overflow-y-auto` again.
          //
          // Which is what makes the floor work: over-tall content now scrolls
          // *inside* `main` instead of being clipped by it, so the page still
          // does not grow. And the floor is needed — with `min-h-0` this reaches
          // `clientHeight: 0` on the two-column layout at any viewport under
          // about 450px tall (1280x400 as readily as 900x440), which is the same
          // fault with a shorter window and is invisible below `md`, where the
          // layout stops asking for `h-full`. Every size is measured in
          // `e2e/layout.spec.ts`: the floor never binds at 1280x720 or 1024x600,
          // and `<html>` scrolls at none of them.
          //
          // ⚠ **`relative` is load-bearing and is not styling.** Every Object
          // button carries an `sr-only` label, and Tailwind's `sr-only` is
          // `position: absolute`. With no positioned ancestor their containing
          // block is the *document*, so each one is placed at its flow position
          // deep inside this scroller — 2,900px down — and stretches `<html>`
          // to reach it. The page then scrolled to empty space on every beat
          // while this list measured perfectly (584 shown, 2,900 of content)
          // and every container above it was correctly bounded. Six fixes were
          // aimed at those containers before the ancestor chain was dumped in
          // one go and showed the escape. See docs/playtest-2026-08-25.md #12.
          className={`relative min-h-0 flex-1 space-y-1 overflow-y-auto text-sm ${mapMode === 'theatre' ? '' : 'md:pr-40'}`}
        >
          {messages.map((m) => (
            <li
              key={m.id}
              className={[
                m.pending ? 'opacity-60' : '',
                m.kind === 'whisper' || m.kind === 'cant' || m.author === DM_AUTHOR
                  ? 'italic'
                  : '',
                // The line the X-card would skip, while the button is hovered or focused.
                vetoPreview && lastDmLine?.id === m.id ? 'rounded bg-amber-500/15 ring-1 ring-amber-500/60' : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              <span className="font-medium">{m.author}:</span>{' '}
              {/* Marked, because the room saw that this was said and not what
                  — a reader who could not tell cant from ordinary speech would
                  not know why half the table did not react. */}
              {m.kind === 'cant' && (
                <span className="text-xs text-neutral-500 dark:text-neutral-400">(in cant) </span>
              )}
              <Mention text={m.text} name={myName} />
              {/* **Marked where it is read.** The beat stays — that is the
                  promise — but a set-aside one that looked identical to a
                  standing one would make the whole objection invisible to
                  anyone who was not watching when the vote closed. */}
              {m.voided && (
                <span className="ml-1 text-xs text-neutral-500 dark:text-neutral-400">(set aside by the table)</span>
              )}
              {/* **The objection, on the beat it is about.** A disputed call
                  goes to the table — any player, on any beat the machine
                  produced. Offered only on the
                  DM's and the engine's lines: an objection against another
                  player's chat is an argument with a person, which is not what
                  this is for and not something a majority should settle.

                  Not offered while a line is `pending`, because its `id` is
                  still the optimistic client id and the server would refuse a
                  message id it has never seen. Withdrawn after
                  `OBJECT_LINK_WINDOW_MS` — a courtesy mirror of the server's
                  own 60-second cutoff, not the real gate: a stale click still
                  gets refused there regardless of what this shows. Opens the
                  confirm dialog below, which fires the Stage-0 claim itself
                  the instant it mounts — see `ObjectionConfirmDialog`. */}
              {!m.pending && !m.live && !m.voided && !openObjection && !confirmingObjection && isObjectable(m, now) && (m.author === DM_AUTHOR || m.kind === 'system') && (
                <button
                  type="button"
                  onClick={() => {
                    clearObjectionClaim();
                    setConfirmingObjection(m.id);
                  }}
                  className="ml-1 align-baseline text-xs text-neutral-400 underline hover:text-neutral-600 dark:hover:text-neutral-300"
                >
                  Object
                  <span className="sr-only"> to this beat — the table votes on whether it stands</span>
                </button>
              )}
            </li>
          ))}
          {/* **An `<li>`, not a `<div>`.** A list may only directly contain
              list items, and a screen reader announces position and length
              from that — a stray child makes "message 4 of 12" wrong. It is
              the scroll anchor and shows nothing, so it is hidden from the
              accessibility tree rather than announced as an empty item. */}
          <li ref={bottom} aria-hidden="true" />
        </ol>
        </div>

        {/* A surfaced attempt, while it resolves. An ambient or concealed one
            sends nothing at all — not even "someone does something", which is
            how a concealed attempt leaks. */}
        <p className="min-h-4 text-xs text-neutral-500 dark:text-neutral-400" aria-live="polite">
          {pending ?? ''}
        </p>

        {/* **By the composer, not above the transcript.** Rendered at the top it
            reads as the first line of the story and sits there for the rest of
            the session; a refusal belongs next to the thing that was refused,
            and has to be dismissable — otherwise the one about a spell slot is
            still on screen an hour later. */}
        {lastError && (
          <p
            role="alert"
            className="flex items-start justify-between gap-2 rounded-md border border-amber-400 bg-amber-50 px-2 py-1 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200"
          >
            <span>{lastError}</span>
            <button
              type="button"
              onClick={clearError}
              aria-label="Dismiss"
              className="text-amber-700 dark:text-amber-400"
            >
              ×
            </button>
          </p>
        )}

        {!amSpirit && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            // The verb token is what decides the channel. Untagged text is
            // table-talk and never reaches the director; a tagged line is an
            // action whose free text is elaboration only.
            if (verb) {
              submitAction(
                verb.verb,
                draft,
                targetId || undefined,
                verb.verb === 'talk' ? stance || undefined : undefined,
              );
              setVerb(undefined);
              setTargetId('');
              setStance('');
            } else if (whisperTo === CANT) {
              // Cant is table talk with the audience the *rules* decide, so it
              // takes the same box and sends no target at all.
              speakCant(draft);
            } else if (whisperTo) {
              // A whisper is table talk with a shorter audience, so it takes
              // the same box — and the target stays selected, because whispers
              // come in conversations rather than one at a time.
              whisper(draft, [whisperTo]);
            } else {
              say(draft);
            }
            setDraft('');
          }}
          // ⚠ **`relative` is load-bearing here too, on the same footing as the
          // transcript `<ol>`'s own fix.** This form carries three `sr-only`
          // labels (`action-target`, `chat-audience`, `chat-input`), and
          // Tailwind's `sr-only` is `position: absolute`. With no positioned
          // ancestor their containing block is the *document*, so each lands
          // at its flow position — which, on a page tall enough (a long
          // "While you were away" digest is what made it visible), sits well
          // past the viewport and stretches `<html>` to reach it: the exact
          // shape of docs/playtest-2026-08-25.md's #12, in a different form
          // this time (`chat-audience`'s "Who can read this" label, not an
          // Object button), and it went unfound because that fix was scoped
          // to the transcript specifically. See docs/playtest-2026-09-14.md #1.
          className="relative flex flex-wrap items-center gap-2"
        >
          {verb && (
            <span className="flex items-center gap-1 rounded-md border border-emerald-600 bg-emerald-50 px-2 py-1 text-sm dark:bg-emerald-950">
              {verbLabel(verb.verb)}
              <button
                type="button"
                onClick={() => setVerb(undefined)}
                aria-label={`Remove the ${verbLabel(verb.verb)} action`}
                className="text-neutral-500 dark:text-neutral-400"
              >
                ×
              </button>
            </span>
          )}
          {/* Offered, never required: most of what a party does is aimed at
              something the world model has no row for — a loose board, a
              smell — and demanding a canon id would make the verb bar
              unusable for exactly those. The list is filtered per viewer by
              the server's one visibility query. */}
          {/* **How the party is carrying itself, and the players say so.**
              Table 10.7 is cross-referenced against "the PCs' disposition
              toward the opposing party" — a fact about how the party is
              behaving, which the party is the only one who knows. A single
              arbiter picking that column would be answering on the players'
              behalf. Blank is indifferent, which is what walking up and
              speaking is. */}
          {verb?.verb === 'talk' && reactionDispositions.length > 0 && (
            <>
              <label className="sr-only" htmlFor="action-stance">
                How the party approaches
              </label>
              <select
                id="action-stance"
                value={stance}
                onChange={(e) => setStance(e.target.value)}
                className="rounded-md border border-neutral-300 bg-white px-2 py-1 text-sm dark:border-neutral-700 dark:bg-neutral-900"
              >
                <option value="">approaching plainly</option>
                {reactionDispositions.map((option) => (
                  <option key={option} value={option}>
                    {option.replace('ifPCs', 'the party is ').toLowerCase()}
                  </option>
                ))}
              </select>
            </>
          )}
          {verb && targets.length > 0 && (
            <>
              <label className="sr-only" htmlFor="action-target">
                Target
              </label>
              <select
                id="action-target"
                value={targetId}
                onChange={(e) => setTargetId(e.target.value)}
                className="min-w-0 max-w-full rounded-md border border-neutral-300 bg-white px-2 py-1 text-sm dark:border-neutral-700 dark:bg-neutral-900"
              >
                <option value="">anything (say what below)</option>
                {byType(targets).map(([type, entries]) => (
                  <optgroup key={type} label={type}>
                    {entries.map((target) => (
                      <option key={target.id} value={target.id} title={target.description}>
                        {target.name}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </>
          )}
          {/* **Whispering had no way in.** The event has been in the contract,
              handled, audience-filtered and rendered since Phase 4, and no
              client could send one — so the only private line anybody ever saw
              was one the engine wrote. Hidden while a verb is attached: an
              action's audience is the engine's to decide from the tier, and
              two answers to "who sees this" is the one thing that must not
              exist. */}
          {!verb && whisperTargets.length > 0 && (
            <>
              <label className="sr-only" htmlFor="chat-audience">
                Who can read this
              </label>
              <select
                id="chat-audience"
                value={whisperTo}
                onChange={(e) => setWhisperTo(e.target.value)}
                className="rounded-md border border-neutral-300 bg-white px-2 py-1 text-sm dark:border-neutral-700 dark:bg-neutral-900"
              >
                <option value="">everyone</option>
                {/* **An audience the rules decide, not the player.** Cant is
                    the thief's own channel: the room is told that it happened
                    and the thieves are told what was said, and neither list is
                    this client's to choose. Shown only to a thief, split on
                    the slash because the roster renders a multi-classed
                    character as `fighter/thief`. */}
                {amThief && <option value={CANT}>in thieves&rsquo; cant</option>}
                {whisperTargets.map((entry) => (
                  <option key={entry.characterId} value={entry.characterId}>
                    whisper {entry.name}
                  </option>
                ))}
              </select>
            </>
          )}
          <label className="sr-only" htmlFor="chat-input">
            {verb ? 'Describe the action' : 'Say something'}
          </label>
          <input
            id="chat-input"
            value={draft}
            onChange={(e) => {
              const next = e.target.value;
              // `/look at the gull` attaches the token and leaves the rest as
              // elaboration — the same fields the button fills, and the token
              // is visible and removable before anything is sent. Only once a
              // space follows the verb: `/look` alone is still being typed.
              const command = readSlashCommand(next, verbs);
              if (command) {
                setVerb(command.verb);
                setDraft(command.rest);
                return;
              }
              setDraft(next);
            }}
            placeholder={
              verb
                ? // Never the tier as jargon: a concealed verb simply reads as
                  // private.
                  `…what exactly?${verb.tier === 'concealed' ? ' (private)' : ''}`
                : whisperTo === CANT
                  ? 'In cant — the room sees only that you spoke…'
                  : whisperTo
                    ? `Whisper to ${whisperTargets.find((e) => e.characterId === whisperTo)?.name ?? 'them'}…`
                    : 'Say something…'
            }
            className="min-w-40 flex-1 rounded-md border border-neutral-300 bg-white px-3 py-2 text-sm dark:border-neutral-700 dark:bg-neutral-900"
          />
          {/* Discovery as much as autocomplete: thirteen of the nineteen verbs
              live behind a "More" toggle, and a player who has never opened it
              does not know `appraise` exists. */}
          {suggestions.length > 0 && (
            <ul className="flex w-full flex-wrap gap-1" aria-label="Matching actions">
              {suggestions.map((option) => (
                <li key={option.verb}>
                  <button
                    type="button"
                    onClick={() => {
                      setVerb(option);
                      setDraft('');
                    }}
                    className="rounded-md border border-neutral-300 px-2 py-0.5 text-xs dark:border-neutral-700"
                  >
                    /{option.verb}{' '}
                    <span className="text-neutral-500 dark:text-neutral-400">{verbCostLabel(option)}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          <button
            type="submit"
            className="rounded-md border border-neutral-300 px-4 py-2 text-sm font-medium dark:border-neutral-700"
          >
            {verb ? 'Do it' : 'Send'}
          </button>
        </form>
        )}

        {/* In combat the verb bar is replaced by the declaration window: the
            two closed sets are different vocabularies, and there is no attack
            verb in the exploration one. */}
        {!amSpirit &&
          (combat ? (
          <CombatPanel
            combat={combat}
            actions={combatActions}
            characterId={myCharacterId ?? undefined}
            onDeclare={(declaration) => {
              declareCombat(declaration);
              // One-shot: a submitted move is spent, and leaving the last
              // click armed would silently resubmit it as the destination
              // for a completely different action next round.
              setPendingDestination(null);
            }}
            sceneFeatures={sceneFeatures}
            memorised={unspent(spells)}
            // Nobody is at a keyboard for a henchman, so their patron declares
            // for them. Only ever this player's own — the server re-checks it,
            // because a character id on the wire is otherwise a player choosing
            // whose turn to take.
            retainers={retainersInFight}
            actingAs={actingAs}
            onChoose={setActingChoice}
            pendingDestination={pendingDestination}
            onNeedsDestinationChange={setNeedsDestination}
          />
          ) : (
            <VerbBar verbs={verbs} selected={verb} onSelect={setVerb} />
          ))}

        {/* The check bar. Roll-under an ability score — the opposite
            comparison from a saving throw — and every modifier is resolved
            server-side, because they land on the score rather than the roll. */}
        {!amSpirit && (
        <CheckBar
          skills={(hireOptions?.skills ?? []).filter((skill) => ownSkills.has(skill.name))}
          selected={checking}
          onSelect={setChecking}
          blind={blindCheck}
          onBlind={setBlindCheck}
          group={groupCheck}
          onGroup={setGroupCheck}
          onCheck={() => {
            const what = checkPayload(checking);
            if (what) rollCheck(what, blindCheck, groupCheck);
          }}
        />
        )}

        {/* The dice bar — and the X-card, which stays for a spirit.
            `dice:roll` is behind `requireLiving` and `player:veto` is behind
            `requireMember`, which is the whole distinction: the dice belong to
            the character and the safety tool belongs to the *player*, exactly
            as the notes do. */}
        <div className="flex flex-wrap items-center gap-2">
          {!amSpirit && (
            <>
              <label className="sr-only" htmlFor="dice-count">
                How many dice
              </label>
              <input
                id="dice-count"
                type="number"
                min={1}
                max={MAX_DICE_BOX}
                inputMode="numeric"
                value={diceCountText}
                onChange={(e) => setDiceCountText(e.target.value)}
                title="How many dice the next click rolls"
                className="w-14 rounded-md border border-neutral-300 bg-white px-2 py-1 text-sm dark:border-neutral-700 dark:bg-neutral-900"
              />
              <span aria-hidden="true" className="-ml-1 text-sm text-neutral-500 dark:text-neutral-400">×</span>
            </>
          )}
          {!amSpirit &&
            [4, 6, 8, 10, 12, 20, 100].map((sides) => (
            <button
              key={sides}
              type="button"
              onClick={() => rollDice(sides, diceCount)}
              className="rounded-md border border-neutral-300 px-3 py-1 text-sm dark:border-neutral-700"
            >
              {dieLabel(diceCount, sides)}
            </button>
          ))}

          {/* **The X-card, which had no button at all.** The server side has
              been complete for a phase — it cancels the beat being streamed,
              records the topic as a standing boundary on every future prompt,
              and holds a per-player cooldown — and nothing in any client could
              raise one, so the whole safety tool was unreachable.

              It sends **no actor**: the server never persists or emits who
              pressed it, which is what makes it usable. Deliberately plain and
              always visible, never behind a menu — a tool you have to go
              looking for is one nobody uses at the moment they need it. */}
          <span className="ml-auto flex items-center gap-2">
            {vetoUndoOpen && (
              <button
                type="button"
                onClick={() => {
                  undoVeto();
                  setVetoUndoOpen(false);
                }}
                className="text-xs underline text-amber-800 dark:text-amber-300"
              >
                Skipped — undo
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                raiseVeto();
                setVetoUndoOpen(true);
              }}
              title={xCardPreview(lastDmLine?.text)}
              onMouseEnter={() => setVetoPreview(true)}
              onMouseLeave={() => setVetoPreview(false)}
              onFocus={() => setVetoPreview(true)}
              onBlur={() => setVetoPreview(false)}
              className="rounded-md border border-amber-700/60 px-3 py-1 text-sm text-amber-800 dark:text-amber-300"
            >
              {X_CARD_LABEL}
            </button>
          </span>
        </div>
      </section>

      {/* Scrolls inside itself rather than pushing the page taller: these
          panels expanded are longer than the viewport on their own, and the
          party list underneath them is the thing you were trying to reach. */}
      {/* **Two bounds on purpose.** `h-full` makes the column exactly as tall as its slot, which is
          the right size at every window height; the viewport `max-h` below is the safety net for
          when that percentage cannot resolve. It used to be the ONLY bound, and "viewport minus 9rem"
          is a guess at how much the header, tab bar and footer take: it was about 37px too tall, so
          the tab wrappers overflowed the page's own scroll area and the Map tab showed two scrollbars.
          The other tab wrappers below carry the same pair.
          ⚠ **`max-h` and not just `min-h-0`.** This column scrolls internally
          only while its row has a definite height, and when that resolution
          failed — anywhere up the chain — the aside grew to its content
          instead, stretched the document to 3,000px and dragged the scroll
          position on every beat. One expanded panel in here (the world import,
          2,620px) was enough to make the table unusable, and collapsing it by
          hand is not a fix because the next one does it again. The viewport
          bound cannot fail the same way: whatever is open in here, the column
          scrolls itself and the page does not.
          ⚠ The underscores are required. `calc(100vh-9rem)` is invalid CSS —
          `calc` needs whitespace around the minus — and Tailwind emits the
          arbitrary value verbatim, so the browser drops the whole declaration
          silently. Written without them this fix did nothing at all and looked
          exactly like the class not applying. */}
      <aside className="space-y-3 md:h-full md:max-h-[calc(100vh_-_9rem)] md:min-h-0 md:overflow-y-auto md:pr-1">
        {/* The one thing a dead player can still do. */}
        {campaignId &&
          roster.find((entry) => entry.characterId === myCharacterId)?.deathStatus === 'dead' && (
            <SpiritPanel campaignId={campaignId} onMovedOn={() => connect(campaignId)} />
          )}
        {/* **Everybody sees it.** What is in it is a nudge anybody may give
            and a policy for your *own* seat. The roster is narrowed to what
            this viewer may direct, because the server gates the policy on
            `actingCharacter` and a control that can only be refused is worse
            than no control.
            Unconditional again — the tab strip that used to live here moved
            to the top of the whole page, and "Table" (this whole two-column
            view) is one of its tabs now, so there is nothing left to gate. */}
        <TablePanel
          roster={roster.filter((r) => r.characterId === myCharacterId)}
          combat={combat}
          onCommand={hostCommand}
        />
        {/* **Shops are somebody in the world** (Maz, 2026-10-20): a shopkeeper standing where the party is. Renders
            nothing at all when nobody here is selling. Re-asked when the party moves or the DM speaks (that is
            when a shopkeeper appears), and after anything changes on a sheet (a purchase moves the purse). */}
        {campaignId && (
          <ShopsPanel
            campaignId={campaignId}
            characterId={myCharacterId ?? null}
            retainers={myRetainers}
            token={`${partyLocation ?? ''}|${sheetVersion}|${worldBeats}`}
          />
        )}
        {/* The presser's own acknowledgement, and nobody else's screen: a
            second press inside the cooldown announces nothing to the room by
            design, so this is the only thing that tells them it was heard. */}
        {/* **The vote, where the table can see it.** `aria-live` because it
            appears without anybody navigating to it, and the whole point is
            that everyone present gets the chance to answer. There is no
            countdown drawn: the deadline is the server's and a client clock
            disagreeing with it would show a vote as closed while it was open. */}
        {/* **What the table is being asked to agree to.** The four judgements
            about one character are the table's now, settled by a majority of
            those present. What is *being* proposed was said in the room as a
            sentence when it opened — this is only the way in. */}
        {openProposal && (
          <div
            aria-live="polite"
            className="rounded-md border border-emerald-700/50 p-2 text-xs text-emerald-900 dark:text-emerald-200"
          >
            <p className="font-medium">
              {openProposal.summary ?? 'The table is being asked to agree to something.'}
            </p>
            <p className="mt-1 text-neutral-500 dark:text-neutral-400">
              The table is voting. A majority of those present carries it
              {openProposal.subjectCharacterId ? '; the character it is about does not vote.' : '.'}
            </p>
            {openProposal.subjectCharacterId === myCharacterId ? (
              /* Said plainly rather than showing buttons that would be refused:
                 every one of the four is a benefit to the character it names. */
              <p className="mt-1">This one is about your character, so it is not yours to vote on.</p>
            ) : openProposal.voted ? (
              <p className="mt-1">Your vote is in.</p>
            ) : (
              <div className="mt-1 flex gap-2">
                <button
                  type="button"
                  onClick={() => voteOnProposal(true)}
                  className="rounded-md border border-emerald-700/60 px-2 py-0.5"
                >
                  Agree
                </button>
                <button
                  type="button"
                  onClick={() => voteOnProposal(false)}
                  className="rounded-md border border-neutral-400/60 px-2 py-0.5"
                >
                  Decline
                </button>
              </div>
            )}
          </div>
        )}
        {confirmingObjection && (
          <ObjectionConfirmDialog
            claim={objectionClaim}
            onClaim={() => claimObjectionLink(confirmingObjection)}
            onConfirm={() => {
              raiseObjection(confirmingObjection);
              setConfirmingObjection(null);
              clearObjectionClaim();
            }}
            onCancel={() => {
              setConfirmingObjection(null);
              clearObjectionClaim();
            }}
          />
        )}
        <ObjectionPanel
          openObjection={openObjection}
          objectionNotice={objectionNotice}
          myName={myName}
          onSubmitGrounds={submitObjectionGrounds}
          onVote={voteOnObjection}
        />
        {vetoNotice && (
          <p className="text-xs text-amber-700 dark:text-amber-300">{vetoNotice}</p>
        )}
        {sheetNotice && (
          // Audienced to the owner: the *fact* of a level-up is public and in
          // the transcript; the numbers are yours.
          <p className="text-xs text-emerald-700 dark:text-emerald-400">{sheetNotice}</p>
        )}
        {revealed.length > 0 && (
          <p className="text-xs text-neutral-500 dark:text-neutral-400">
            {/* The map hides an unrevealed region's waypoints, so the names
                are listed here too — a region with nothing pinned in it is
                otherwise invisible even after it is cleared. Fog only ever
                uncovers, so this list only grows. */}
            Revealed: {revealed.join(', ')}
          </p>
        )}
        <SheetLink characterId={myCharacterId} />

        <h2 className="text-sm font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Party</h2>
        <ul className="space-y-1 text-sm">
          {roster.map((entry) => (
            <li key={entry.characterId} className="flex items-start gap-2">
              <span
                title={entry.presence}
                className={`mt-1.5 inline-block h-2 w-2 shrink-0 rounded-full ${
                  entry.presence === 'online'
                    ? 'bg-emerald-500'
                    : entry.presence === 'idle'
                      ? 'bg-amber-400'
                      : entry.presence === 'away'
                        ? 'bg-amber-600'
                        : 'bg-neutral-500'
                }`}
                aria-hidden
              />
              <span className="min-w-0">
                <span className="block truncate font-medium">
                  {entry.name}
                </span>
                <span className="block text-xs text-neutral-500 dark:text-neutral-400">
                  {entry.characterClass} {entry.level} · {entry.hitPoints.current}/
                  {entry.hitPoints.max} hp
                </span>
                {/* Only the exceptions: absence means with the party, which is
                    almost everybody almost always. A character who scouted
                    ahead and was forgotten about is what this prevents. */}
                {entry.station && (
                  <span className="block text-xs text-amber-700 dark:text-amber-400">
                    {stationLabel(entry.station)}
                  </span>
                )}
                {/* **The Owner's.** `DELETE …/members/:characterId` is
                    `requireOwner`. Your own seat is
                    not removable here either way — giving up your own is the
                    other control, and the two tell the room different things. */}
                {campaignId &&
                  amOwner &&
                  entry.characterId !== myCharacterId && (
                    <RemoveSeat
                      campaignId={campaignId}
                      characterId={entry.characterId}
                      name={entry.name}
                      onRemoved={() => connect(campaignId)}
                    />
                  )}
              </span>
            </li>
          ))}
          {roster.length === 0 && (
            <li className="text-xs text-neutral-500 dark:text-neutral-400">nobody here yet</li>
          )}
        </ul>
        {/* **Asking the DM for a beat is any player's.** The text is quoted
            to the model as a request and never executed as an instruction, so
            it sets no state and decides no outcome. The server rate-limits it,
            because every prompt is a model call. */}
        {!amSpirit && (
          /* **Its own input, its own words, and an answer when pressed.**
             This was a bare button that silently borrowed the *chat* draft box
             and, when that was empty, sent a canned sentence — so a player who
             typed a chat line and pressed this had it repurposed as a DM
             prompt, and a player who pressed it on an empty box fired a real
             model call with no idea that was what happened. The only statement
             of any of that was a `title` tooltip: invisible on touch, unnoticed
             on desktop.

             Three faults, three fixes: an input of its own so nothing is
             borrowed; visible copy saying what the button is for; and an
             acknowledgement, because the call is fired un-awaited and a working
             press was indistinguishable from a dead one for as long as a local
             12B takes — which teaches pressing again, and every press is a
             model call. */
          <form
            className="space-y-1"
            onSubmit={(e) => {
              e.preventDefault();
              const asked = dmAsk.trim();
              if (asked === '' || dm.typing) return;
              promptDm(asked);
              setDmAsk('');
            }}
          >
            <label className="block text-xs text-neutral-500 dark:text-neutral-400" htmlFor="ask-dm">
              Ask the DM to narrate a beat — say what you want described.
            </label>
            <div className="flex gap-1">
              <input
                id="ask-dm"
                value={dmAsk}
                onChange={(e) => setDmAsk(e.target.value)}
                maxLength={200}
                placeholder="What does the room smell like?"
                className="min-w-0 flex-1 rounded-md border border-neutral-300 bg-white px-2 py-1 text-xs dark:border-neutral-700 dark:bg-neutral-900"
              />
              <button
                type="submit"
                disabled={dmAsk.trim() === '' || dm.typing}
                className="rounded-md border border-neutral-300 px-3 py-1 text-xs disabled:opacity-40 dark:border-neutral-700"
              >
                {dm.typing ? 'Writing…' : 'Ask'}
              </button>
            </div>
            {/* **The refusal, next to the thing that was refused.** The error
                banner deliberately sits by the composer — a refusal about a
                spell slot belongs there — but this control is in the sidebar,
                so its own refusal was appearing somewhere the presser was not
                looking. That is the veto-cooldown shape, already fixed once for
                exactly this reason: the button appears to do nothing, so it
                gets pressed again, and every press is a model call.

                Matched on the server's own sentence rather than a new field on
                the error, because widening the contract for one banner is a
                worse trade than one narrow string — and if it ever stops
                matching, the banner by the composer still carries it. */}
            {lastError?.includes('the DM was just asked') && (
              <p role="status" className="text-xs text-amber-700 dark:text-amber-400">
                {lastError}
              </p>
            )}
          </form>
        )}
        {/* **Putting one of the four to the table.** Each is a call about a
            specific person's character, and a majority of those present is who
            makes it. Only offered when
            nothing else is being voted on, because one open proposal per table
            is what the database enforces — a second press would be refused. */}
        {!amSpirit && !openProposal && myCharacterId && (
          <details className="rounded-md border border-neutral-300 p-2 text-xs dark:border-neutral-700">
            <summary className="cursor-pointer text-neutral-500 dark:text-neutral-400">Put something to the table</summary>
            <div className="mt-2 space-y-1">
              <p className="text-neutral-500 dark:text-neutral-400">
                These four are put to the table as a vote. Three are about one character, so pick whose first; an
                experience award can go to the whole party.
              </p>
              <label className="sr-only" htmlFor="propose-subject">
                Whose character
              </label>
              <select
                id="propose-subject"
                value={proposeSubject}
                onChange={(e) => setProposeSubject(e.target.value)}
                className="w-full rounded-md border border-neutral-300 bg-white px-2 py-1 dark:border-neutral-700 dark:bg-neutral-900"
              >
                <option value="">The whole party (experience only)…</option>
                {roster.map((entry) => (
                  <option key={entry.characterId} value={entry.characterId}>
                    {entry.name}
                  </option>
                ))}
                {/* Henchmen, for "Offer a place at the table" — the one
                    proposal kind whose subject is never a seated party
                    member, and the only one a henchman can be named for. */}
                {allHenchmen.map((entry) => (
                  <option key={entry.characterId} value={entry.characterId}>
                    {entry.name} (henchman)
                  </option>
                ))}
              </select>
              <input
                value={proposeAmount}
                onChange={(e) => setProposeAmount(e.target.value)}
                inputMode="numeric"
                aria-label="Experience to award"
                placeholder="how much experience, for Award experience only"
                className="w-full rounded-md border border-neutral-300 bg-white px-2 py-1 dark:border-neutral-700 dark:bg-neutral-900"
              />
              <div className="flex flex-wrap gap-1">
                {PROPOSAL_BUTTONS.map(({ kind, label, help }) => {
                  const blocked = proposalBlocker(
                    kind,
                    proposeSubject,
                    allHenchmen.some((h) => h.characterId === proposeSubject),
                    proposeAmount,
                  );
                  return (
                    <button
                      key={kind}
                      type="button"
                      disabled={blocked !== undefined}
                      title={blocked ?? help}
                      onClick={() => {
                        const amount = Number.parseInt(proposeAmount, 10);
                        propose(
                          kind,
                          kind === 'award_xp' && Number.isFinite(amount) ? { amount } : {},
                          proposeSubject,
                        );
                        setProposeAmount('');
                        setProposeSubject('');
                      }}
                      className="rounded-md border border-neutral-300 px-2 py-0.5 disabled:opacity-40 dark:border-neutral-700"
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
              <p className="text-neutral-500 dark:text-neutral-400">
                {proposeSubject === ''
                  ? 'Finish training, Offer a place and Raise a stronghold need a character: choose one above.'
                  : 'A greyed-out button is not possible for this choice: hover it to see why.'}
              </p>
            </div>
          </details>
        )}
        {campaignId && <LeaveTable campaignId={campaignId} />}
      </aside>
    </div>
      )}

      {/* **`WorldMap` is everyone's, at any time, independent of `mapMode`.**
          It takes the same data the shared, in-scene map (inside the Table
          tab) reads, so any character can check the regional map here
          regardless of what the rest of the table is currently looking at —
          and now at the same width the transcript gets, not a narrow strip.
          **`MapAdminPanel` is `amOwner`-gated — see its own docstring for
          why**, including the fog-boundary argument that once justified
          showing it to everyone and why that argument answers a different
          question than this gate does. `onPickPoint` is `amOwner`-only for
          the same reason: only the Owner's click can be saved, so nobody
          else's click should look like it might do anything. */}
      {sidebarTab === 'map' && campaignId && (
        <div className="space-y-3 md:h-full md:min-h-0 md:max-h-[calc(100vh_-_9rem)] md:overflow-y-auto">
          <WorldMap
            map={map}
            waypoints={waypointsOf(places)}
            lines={mapLines}
            {...(terrain ? { terrain } : {})}
            {...(partyLocation ? { partyLocation } : {})}
            {...(amOwner ? { onPickPoint: setPicked } : {})}
            {...(combat === null
              ? {
                  campaignId,
                  // And back to the story: the journey's lines are written on the Table tab.
                  onTravel: (placeId: string) => {
                    submitAction('move', '', placeId, undefined);
                    setSidebarTab('table');
                  },
                }
              : {})}
          />
          {amOwner && (
          <MapAdminPanel
            campaignId={campaignId}
            inCombat={combat !== null}
            worldMapUrl={map.worldMapUrl}
            picked={picked}
            onPickedUsed={() => setPicked(undefined)}
            onCommand={hostCommand}
            onPlacesChanged={reloadPlaces}
            revealed={revealed}
            pinRegions={[...new Set(waypointsOf(places).map((w) => w.regionId))]}
            encounterMapScaleFeetPerPercent={combat?.mapScaleFeetPerPercent}
            // An import rewrites the ground as well as the lines over it,
            // and the two are separate requests — so the one act reloads
            // both, or the rivers arrive over nothing.
          />
          )}
        </div>
      )}

      {/* Retainers. Everyone sees who is retained; you hire only for a
          character you may direct, which `directableCharacter` decides. */}
      {sidebarTab === 'henchmen' && campaignId && (
        <div className="md:h-full md:min-h-0 md:max-h-[calc(100vh_-_9rem)] md:overflow-y-auto">
          <HenchmenPanel
            campaignId={campaignId}
            view={henchmen}
            myCharacterId={myCharacterId}
            options={hireOptions}
            onCommand={hostCommand}
            onChanged={() => setHenchmenVersion((n) => n + 1)}
          />
        </div>
      )}

      {/* The payroll, separately. A hireling is a different arrangement, not a
          cheaper henchman — a wage, no sheet, and outside the Charisma cap. */}
      {sidebarTab === 'hirelings' && campaignId && (
        <div className="md:h-full md:min-h-0 md:max-h-[calc(100vh_-_9rem)] md:overflow-y-auto">
          <HirelingsPanel campaignId={campaignId} {...(myCharacterId ? { myCharacterId } : {})} />
        </div>
      )}

      {/* **The viewer's own, never the acting character's.** This panel's
          Study/pray writes through `spell:memorise`, which acts on the sender
          — so a retainer's loadout rendered here would be a wrong label over
          a wrong write. Not reachable at all for a spirit — `sidebarTabsFor`
          drops the tab itself rather than showing an empty pane, the same
          gate `spell:memorise`/`spell:cast` enforce server-side. */}
      {sidebarTab === 'spells' && !amSpirit && (
        <div className="md:h-full md:min-h-0 md:max-h-[calc(100vh_-_9rem)] md:overflow-y-auto">
          <SpellsPanel
            sheet={showingSpells === '' ? ownSpells : retainerSpells}
            onMemorise={memoriseSpells}
            onCast={castSpell}
            // **Not a socket event.** Learning costs no world clock and the
            // table has no stake in the choice, so it is a sheet act over HTTP —
            // and the answer is the only thing that knows what the book now
            // holds, so both sheets are re-read rather than guessed at.
            onLearn={(name, characterId) => {
              if (!campaignId) return;
              void api
                .post(`/api/campaigns/${campaignId}/spells/learn`, {
                  name,
                  ...(characterId ? { characterId } : {}),
                })
                .then(() => {
                  reloadOwnSpells();
                  reloadRetainerSpells();
                })
                .catch(() => undefined);
            }}
            // **Copying costs the table's clock and the wizard's gold**, so the reason
            // for a refusal ("the purse holds 50 gp", "not until the next level") is
            // handed back to the panel rather than swallowed here.
            onCopy={async (name, characterId, fromCharacterId) => {
              const result = await api.post<CopyResult>(`/api/campaigns/${campaignId}/spells/copy`, {
                name,
                ...(characterId ? { characterId } : {}),
                ...(fromCharacterId ? { fromCharacterId } : {}),
              });
              reloadOwnSpells();
              reloadRetainerSpells();
              return result;
            }}
            // **Consent for a book is the owner's, given in advance** and spent by the
            // copy; buying another blank book is what a full one is answered with.
            onLend={async (spell, toCharacterId, characterId) => {
              await api.post(`/api/campaigns/${campaignId}/spells/lend`, {
                spell,
                toCharacterId,
                ...(characterId ? { characterId } : {}),
              });
              reloadOwnSpells();
              reloadRetainerSpells();
            }}
            onUnlend={async (spell, toCharacterId, characterId) => {
              await api.delete(`/api/campaigns/${campaignId}/spells/lend`, {
                spell,
                toCharacterId,
                ...(characterId ? { characterId } : {}),
              });
              reloadOwnSpells();
              reloadRetainerSpells();
            }}
            onBuyBook={async (characterId) => {
              const bought = await api.post<{ capacity: number; costCp: number }>(
                `/api/campaigns/${campaignId}/spells/book`,
                characterId ? { characterId } : {},
              );
              reloadOwnSpells();
              reloadRetainerSpells();
              return `Bought another blank book — the book now holds ${bought.capacity} pages.`;
            }}
            inCombat={combat !== null}
            // The retainers a patron may study for. A hired caster's slots refill
            // no other way — they have no login to ask with.
            casters={myRetainers}
            showing={showingSpells}
            onShow={setShowingSpells}
          />
        </div>
      )}

      {/* `SettingsPanel` is Owner-only by its own docstring (`PATCH …/config`
          is `requireOwner`) — gated the same reason and the same way
          `MapAdminPanel` is above: a form every other player can see but
          not use is the exact UX cost Maz rejected when this table's admin
          surfaces were split out into their own tabs. */}
      {sidebarTab === 'settings' && campaignId && amOwner && (
        <div className="md:h-full md:min-h-0 md:max-h-[calc(100vh_-_9rem)] md:overflow-y-auto">
          <SettingsPanel campaignId={campaignId} />
        </div>
      )}

      {sidebarTab === 'notes' && campaignId && (
        <div className="md:h-full md:min-h-0 md:max-h-[calc(100vh_-_9rem)] md:overflow-y-auto">
          <NotesPanel campaignId={campaignId} onSave={saveNotes} />
        </div>
      )}
      </div>
    </div>
  );
}
